import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { replaceMicrophoneTrack } from "../lib/audioDevices";
import {
  appendTranscriptDelta,
  shouldStartTranscriptParagraph,
  type TranscriptDeltaTiming,
} from "../lib/transcript";
import {
  connectRealtimeTranslation,
  type RealtimeTranslationSession,
  type RealtimeTranslationStatus,
} from "../lib/realtimeTranslation";
import { reportRealtimeTelemetry } from "../lib/realtimeTelemetry";
import type { TargetLanguage } from "../shared/languages";
import type {
  RealtimeSessionEndReason,
  RealtimeTelemetryEvent,
  RealtimeTransport,
} from "../shared/realtimeTelemetry";

const RETRY_DELAYS = [500, 1000, 2000] as const;

type ConnectOptions = Parameters<typeof connectRealtimeTranslation>[0];

export type RealtimeHookDependencies = {
  connect?: (options: ConnectOptions) => Promise<RealtimeTranslationSession>;
  createAttemptStream?: (source: MediaStream) => MediaStream;
  wait?: (delay: number, signal: AbortSignal) => Promise<void>;
  fetchImpl?: typeof fetch;
  now?: () => number;
  reportTelemetry?: (event: RealtimeTelemetryEvent) => Promise<void> | void;
};

export type UseRealtimeTranslationResult = {
  status: RealtimeTranslationStatus;
  transcript: string;
  microphoneEnabled: boolean;
  audioEnabled: boolean;
  error: string | null;
  start(language: TargetLanguage, stream: MediaStream): Promise<void>;
  retry(): Promise<void>;
  setMicrophoneEnabled(value: boolean): void;
  setAudioEnabled(value: boolean): void;
  replaceMicrophone(stream: MediaStream): Promise<void>;
  stop(): void;
};

export async function requestRealtimeClientSecret(
  targetLanguage: TargetLanguage,
  fetchImpl: typeof fetch = fetch,
): Promise<{
  value: string;
  expiresAt: number;
  transport: RealtimeTransport;
  telemetrySessionId: string;
}> {
  const response = await fetchImpl("/api/realtime/session", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetLanguage }),
  });
  if (!response.ok) throw new Error("connection");

  const payload = await response.json() as {
    value?: unknown;
    expiresAt?: unknown;
    transport?: unknown;
    telemetrySessionId?: unknown;
  };
  if (
    typeof payload.value !== "string" ||
    !payload.value.trim() ||
    typeof payload.expiresAt !== "number"
    || (payload.transport !== "translation" && payload.transport !== "realtime")
    || typeof payload.telemetrySessionId !== "string"
    || !payload.telemetrySessionId.trim()
  ) {
    throw new Error("connection");
  }
  return {
    value: payload.value,
    expiresAt: payload.expiresAt,
    transport: payload.transport,
    telemetrySessionId: payload.telemetrySessionId,
  };
}

export function useRealtimeTranslation(
  audioElementRef: RefObject<HTMLAudioElement | null>,
  dependencies: RealtimeHookDependencies = {},
): UseRealtimeTranslationResult {
  const [status, setStatus] = useState<RealtimeTranslationStatus>("idle");
  const [transcript, setTranscript] = useState("");
  const [microphoneEnabled, setMicrophoneEnabledState] = useState(true);
  const [audioEnabled, setAudioEnabledState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<RealtimeTranslationSession | null>(null);
  const sourceRef = useRef<MediaStream | null>(null);
  const attemptStreamRef = useRef<MediaStream | null>(null);
  const languageRef = useRef<TargetLanguage>("en");
  const operationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const microphoneEnabledRef = useRef(true);
  const audioEnabledRef = useRef(false);
  const replacementGenerationRef = useRef(0);
  const replacementQueueRef = useRef<Promise<void>>(Promise.resolve());
  const lastTranscriptTimingRef = useRef<TranscriptDeltaTiming | null>(null);
  const activeTelemetryRef = useRef<{
    sessionId: string;
    targetLanguage: TargetLanguage;
    transport: RealtimeTransport;
    connectedAtMs: number;
  } | null>(null);
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const reportCurrentSessionEnd = useCallback((reason: RealtimeSessionEndReason) => {
    const active = activeTelemetryRef.current;
    if (!active) return;
    activeTelemetryRef.current = null;
    const now = (dependenciesRef.current.now ?? Date.now)();
    const durationMs = Math.min(
      24 * 60 * 60 * 1_000,
      Math.max(0, Math.round(now - active.connectedAtMs)),
    );
    void (dependenciesRef.current.reportTelemetry ?? reportRealtimeTelemetry)({
      event: "ended",
      sessionId: active.sessionId,
      targetLanguage: active.targetLanguage,
      transport: active.transport,
      durationMs,
      reason,
    });
  }, []);

  const disposeTransport = useCallback((reason: RealtimeSessionEndReason) => {
    reportCurrentSessionEnd(reason);
    replacementGenerationRef.current += 1;
    replacementQueueRef.current = Promise.resolve();
    sessionRef.current?.close();
    sessionRef.current = null;
    attemptStreamRef.current?.getTracks().forEach(track => track.stop());
    attemptStreamRef.current = null;
  }, [reportCurrentSessionEnd]);

  const cancelOperation = useCallback((reason: RealtimeSessionEndReason) => {
    operationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    disposeTransport(reason);
  }, [disposeTransport]);

  const connectWithRetries = useCallback(async (
    operation: number,
    waitBeforeFirstAttempt: boolean,
  ): Promise<boolean> => {
    let delayIndex: number | null = waitBeforeFirstAttempt ? 0 : null;

    while (operationRef.current === operation) {
      if (delayIndex !== null) {
        if (delayIndex >= RETRY_DELAYS.length) break;
        setStatus("reconnecting");
        const controller = new AbortController();
        abortRef.current = controller;
        await (dependenciesRef.current.wait ?? waitForDelay)(RETRY_DELAYS[delayIndex]!, controller.signal);
        if (controller.signal.aborted || operationRef.current !== operation) return false;
      }

      const source = sourceRef.current;
      if (!source || !hasLiveAudioTrack(source)) {
        setError("no-microphone");
        setStatus("error");
        return false;
      }
      const audioElement = audioElementRef.current;
      if (!audioElement) {
        setError("connection");
        setStatus("error");
        return false;
      }

      const attemptStream = (dependenciesRef.current.createAttemptStream ?? cloneLiveAudioStream)(source);
      const targetLanguage = languageRef.current;
      attemptStream.getAudioTracks().forEach(track => {
        track.enabled = microphoneEnabledRef.current;
      });
      attemptStreamRef.current = attemptStream;

      try {
        lastTranscriptTimingRef.current = null;
        const session = await (dependenciesRef.current.connect ?? connectRealtimeTranslation)({
          microphoneStream: attemptStream,
          audioElement,
          requestClientSecret: () => requestRealtimeClientSecret(
            targetLanguage,
            dependenciesRef.current.fetchImpl,
          ),
          onTranscriptDelta: (delta, elapsedMs) => {
            if (operationRef.current === operation) {
              const timing: TranscriptDeltaTiming = {
                elapsedMs,
                receivedAtMs: (dependenciesRef.current.now ?? Date.now)(),
              };
              const startNewParagraph = shouldStartTranscriptParagraph(
                lastTranscriptTimingRef.current,
                timing,
              );
              lastTranscriptTimingRef.current = timing;
              setTranscript(current => appendTranscriptDelta(
                current,
                delta,
                4_000,
                startNewParagraph,
              ));
            }
          },
          onStatusChange: nextStatus => {
            if (operationRef.current === operation) setStatus(nextStatus);
          },
          onDisconnect: () => {
            if (operationRef.current !== operation) return;
            disposeTransport("connection-failed");
            void connectWithRetries(operation, true);
          },
        });
        if (operationRef.current !== operation) {
          session.close();
          return false;
        }
        if (sourceRef.current !== source) {
          session.close();
          if (attemptStreamRef.current === attemptStream) attemptStreamRef.current = null;
          continue;
        }
        sessionRef.current = session;
        activeTelemetryRef.current = {
          sessionId: session.telemetrySessionId,
          targetLanguage,
          transport: session.transport,
          connectedAtMs: (dependenciesRef.current.now ?? Date.now)(),
        };
        void (dependenciesRef.current.reportTelemetry ?? reportRealtimeTelemetry)({
          event: "connected",
          sessionId: session.telemetrySessionId,
          targetLanguage,
          transport: session.transport,
        });
        session.setMicrophoneEnabled(microphoneEnabledRef.current);
        session.setAudioEnabled(audioEnabledRef.current);
        setError(null);
        setStatus(microphoneEnabledRef.current ? "listening" : "muted");
        return true;
      } catch {
        if (attemptStreamRef.current === attemptStream) attemptStreamRef.current = null;
        if (operationRef.current !== operation) return false;
        delayIndex = delayIndex === null ? 0 : delayIndex + 1;
      }
    }

    if (operationRef.current === operation) {
      setError("connection");
      setStatus("error");
    }
    return false;
  }, [audioElementRef, disposeTransport]);

  const start = useCallback(async (language: TargetLanguage, stream: MediaStream) => {
    cancelOperation("language-change");
    const operation = operationRef.current;
    sourceRef.current = stream;
    languageRef.current = language;
    lastTranscriptTimingRef.current = null;
    setTranscript("");
    setError(null);
    setStatus("connecting");
    const connected = await connectWithRetries(operation, false);
    if (!connected && operationRef.current === operation) throw new Error("connection");
  }, [cancelOperation, connectWithRetries]);

  const retry = useCallback(async () => {
    cancelOperation("retry");
    const operation = operationRef.current;
    setError(null);
    setStatus("connecting");
    const connected = await connectWithRetries(operation, false);
    if (!connected && operationRef.current === operation) throw new Error("connection");
  }, [cancelOperation, connectWithRetries]);

  const setMicrophoneEnabled = useCallback((value: boolean) => {
    microphoneEnabledRef.current = value;
    setMicrophoneEnabledState(value);
    sourceRef.current?.getAudioTracks().forEach(track => {
      track.enabled = value;
    });
    attemptStreamRef.current?.getAudioTracks().forEach(track => {
      track.enabled = value;
    });
    sessionRef.current?.setMicrophoneEnabled(value);
    if (sessionRef.current) setStatus(value ? "listening" : "muted");
  }, []);

  const setAudioEnabled = useCallback((value: boolean) => {
    audioEnabledRef.current = value;
    setAudioEnabledState(value);
    if (audioElementRef.current) audioElementRef.current.muted = !value;
    sessionRef.current?.setAudioEnabled(value);
  }, [audioElementRef]);

  const replaceMicrophone = useCallback(async (stream: MediaStream) => {
    if (!hasLiveAudioTrack(stream)) {
      setError("no-microphone");
      return;
    }
    const replacementGeneration = ++replacementGenerationRef.current;
    sourceRef.current = stream;
    const runReplacement = async () => {
      if (replacementGenerationRef.current !== replacementGeneration) return;
      const currentSession = sessionRef.current;
      const oldAttempt = attemptStreamRef.current;
      if (!currentSession || !oldAttempt) return;
      const operation = operationRef.current;
      const replacement = (dependenciesRef.current.createAttemptStream ?? cloneLiveAudioStream)(stream);
      replacement.getAudioTracks().forEach(track => {
        track.enabled = microphoneEnabledRef.current;
      });

      try {
        await replaceMicrophoneTrack(currentSession.sender, oldAttempt, replacement);
        if (
          operationRef.current !== operation ||
          sessionRef.current !== currentSession ||
          attemptStreamRef.current !== oldAttempt
        ) {
          replacement.getTracks().forEach(track => track.stop());
          return;
        }

        // This may be an older request that was already in flight when a newer
        // source was chosen. Record the sender's real track so the queued latest
        // replacement can safely replace it, but only the latest request may
        // publish UI state.
        attemptStreamRef.current = replacement;
        if (replacementGenerationRef.current === replacementGeneration) setError(null);
      } catch {
        replacement.getTracks().forEach(track => track.stop());
        if (
          replacementGenerationRef.current === replacementGeneration &&
          operationRef.current === operation &&
          sessionRef.current === currentSession &&
          attemptStreamRef.current === oldAttempt
        ) {
          setError("no-microphone");
        }
      }
    };

    const queued = replacementQueueRef.current.then(runReplacement, runReplacement);
    replacementQueueRef.current = queued.catch(() => {});
    await queued;
  }, []);

  const stop = useCallback(() => {
    cancelOperation("user-stop");
    sourceRef.current = null;
    lastTranscriptTimingRef.current = null;
    setTranscript("");
    setError(null);
    setStatus("idle");
  }, [cancelOperation]);

  useEffect(() => () => {
    operationRef.current += 1;
    replacementGenerationRef.current += 1;
    abortRef.current?.abort();
    disposeTransport("page-unload");
    sourceRef.current = null;
  }, [disposeTransport]);

  return {
    status,
    transcript,
    microphoneEnabled,
    audioEnabled,
    error,
    start,
    retry,
    setMicrophoneEnabled,
    setAudioEnabled,
    replaceMicrophone,
    stop,
  };
}

function hasLiveAudioTrack(stream: MediaStream): boolean {
  return stream.getAudioTracks().some(track => track.readyState === "live");
}

function cloneLiveAudioStream(source: MediaStream): MediaStream {
  const tracks = source.getAudioTracks().filter(track => track.readyState === "live").map(track => track.clone());
  if (tracks.length === 0) throw new Error("no-microphone");
  return new MediaStream(tracks);
}

function waitForDelay(delay: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    const timer = window.setTimeout(resolve, delay);
    signal.addEventListener("abort", () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}
